/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import {
  object,
  shape,
  string,
  bool,
  array,
  func,
  instanceOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'clsx';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import { showSignupModal as showSignupModalAction } from 'actions/signup';
import {
  getShortlist as getShortlistAction,
  hideShortlistBasket as hideShortlistBasketAction,
  removeTalent as removeTalentAction,
} from 'actions/shortlist';

import Button from 'components/Button/Button';

import Talent from './Talent/Talent';

import styles from './shortlist-basket.scss';

/**
 * This is the ShortlistBasket component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class ShortlistBasket extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      isAuthenticated: bool.isRequired,
      token: string.isRequired,
    }).isRequired,
    shortlist: shape({
      requestlist_id: string.isRequired,
      visible: bool,
      requests: shape({
        talents_by_skill: array,
      }),
    }).isRequired,
    history: object.isRequired,
    getShortlist: func.isRequired,
    hideShortlistBasket: func.isRequired,
    showSignupModal: func.isRequired,
    removeTalent: func.isRequired,
    parentElem: shape({ current: instanceOf(Element) }).isRequired,
  };

  /**
   * Create ShortlistBasket.
   */
  constructor(props) {
    super(props);

    this.wrapper = React.createRef();

    this.timeout = null;
  }

  componentDidMount() {
    const {
      auth: { token },
      shortlist: { requestlist_id },
      getShortlist,
    } = this.props;

    // @NOTE: Should we fetch the requestlist_id from the URL or
    // the requestlist_id on the user/bootsrap?
    // @chris

    if (requestlist_id) {
      getShortlist(token, requestlist_id);
    }

    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { shortlist: prevShortlist } = prevProps;
    const {
      auth: { token },
      shortlist,
      getShortlist,
    } = this.props;

    if (prevShortlist.visible !== shortlist.visible && shortlist.visible) {
      this.wrapper.current.focus();
    }

    if (
      prevShortlist.requestlist_id !== shortlist.requestlist_id &&
      !shortlist.loading &&
      shortlist.requestlist_id
    ) {
      getShortlist(token, shortlist.requestlist_id);
    }

    if (prevShortlist.visible !== shortlist.visible) {
      const intercomApp = document.querySelector('.intercom-app');

      if (intercomApp) {
        intercomApp.style.visibility = shortlist.visible ? 'hidden' : 'visible';
      }
    }
  }

  handleClickOutside = event => {
    const {
      shortlist: { visible },
      hideShortlistBasket,
      parentElem,
    } = this.props;
    const { current } = this.wrapper;

    clearTimeout(this.timeout);

    // If click outside basket, close.
    if (
      current &&
      parentElem?.current &&
      !current.contains(event.target) &&
      !parentElem?.current.contains(event.target) &&
      visible
    ) {
      this.timeout = setTimeout(() => {
        // If still visible after timeout.
        if (this.props.shortlist.visible) {
          hideShortlistBasket();
        }
      }, 120);
    }
  };

  onClickRequest = event => {
    event.preventDefault();

    const {
      auth: { isAuthenticated },
      showSignupModal,
      hideShortlistBasket,
      history,
    } = this.props;

    if (!isAuthenticated) {
      return showSignupModal();
    }

    hideShortlistBasket();

    return history.push(createUrl('jobSendRequests'));
  };

  onClickBrowse = event => {
    event.preventDefault();

    const { hideShortlistBasket } = this.props;

    hideShortlistBasket();
  };

  onRemove = talentId => {
    const {
      auth: { token },
      shortlist: { requestlist_id },
      removeTalent,
    } = this.props;

    removeTalent(token, requestlist_id, talentId);
  };

  renderTalents = () => {
    const {
      auth: { isAuthenticated },
      shortlist: {
        requests: { talents_by_skill },
      },
    } = this.props;

    if (talents_by_skill.length < 0) return null;

    return talents_by_skill.map(({ label, skill, talents }) => (
      <div className={styles.talents} key={skill}>
        <h3 className={styles.label}>{label}</h3>

        {talents.map(talent => (
          <Talent
            {...talent}
            title={isAuthenticated ? talent.title : talent.shortname}
            onRemove={this.onRemove}
            key={talent.talent_id}
          />
        ))}
      </div>
    ));
  };

  /**
   * Render ShortlistBasket.
   *
   * @return {Object}
   */
  render() {
    const {
      shortlist: {
        visible,
        requests: { talent_count },
      },
    } = this.props;

    return (
      <div
        ref={this.wrapper}
        className={classNames(styles.wrapper, {
          [styles.visible]: visible,
        })}
      >
        <div className={styles.content}>
          {talent_count > 0 && (
            <h2 className={styles.title}>{p.t('shortlist.basket.title')}</h2>
          )}

          {talent_count <= 0 && (
            <h2 className={styles.title}>
              {p.t('shortlist.basket.title_empty')}
            </h2>
          )}

          {this.renderTalents()}
        </div>

        <div className={styles.buttons}>
          {talent_count > 0 && (
            <Button
              className={styles.button}
              onClick={this.onClickRequest}
              wide
            >
              {p.t('shortlist.basket.send_request')}
            </Button>
          )}

          <Button
            className={styles.button}
            onClick={this.onClickBrowse}
            wide
            dark
            ghost
            small
          >
            {p.t('shortlist.basket.browser_talents')}
          </Button>
        </div>
      </div>
    );
  }
}

export default withRouter(
  connect(({ auth, user, shortlist }) => ({ auth, user, shortlist }), {
    showSignupModal: showSignupModalAction,
    getShortlist: getShortlistAction,
    hideShortlistBasket: hideShortlistBasketAction,
    removeTalent: removeTalentAction,
  })(ShortlistBasket)
);
